import { memo, useEffect, useRef } from 'react';
import { EmbeddedBlock, EmbeddedBlockVariant } from '@tinct/core-api-models';
import { cn } from '@tinct/core-utils';
import { HtmlBlockSelector } from '../../../landing-page-editor/components/html-block-selector/html-block-selector';
import type { CanvasWheelInput } from './studio-canvas';

export type CanvasArtboardDefinition = {
  /** Dimension key from htmlKeys, e.g. "1200x800". */
  key: string;
  /** The artboard's viewport width in CSS px. */
  width: number;
  /**
   * The key's viewport height in CSS px. The artboard grows with its content,
   * so this is what the document's `vh` units are pinned to.
   */
  height: number;
  /** Human name: Large desktop / Desktop / Phone. */
  label: string;
  /** The packaged-preview URL for this dimension. */
  url: string;
};

type CanvasArtboardProps = {
  definition: CanvasArtboardDefinition;
  html: string;
  isHtmlLoading: boolean;
  blocks: EmbeddedBlock[];
  blockVariants?: EmbeddedBlockVariant[];
  selectedBlockId: string | null;
  onBlockSelect?: (blockId: string | null) => void;
  /** Makes the selected block's badge editable — active artboard only. */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
  /** `text` enables inline text edition — active artboard only, like overlays. */
  interactionMode?: 'select' | 'text';
  onVariantEditCommit?: (blockVariantId: string, html: string) => Promise<string | null | undefined>;
  /**
   * Overlays (observers, badges, rAF loops) only run on the active artboard —
   * the others stay pure iframes. Activation follows the pointer, so clicking
   * any artboard still selects blocks ("full editing" without 3× overlay cost).
   */
  isActive: boolean;
  /** Called with this artboard's key so the parent can pass one stable callback. */
  onActivate: (key: string) => void;
  /**
   * Wheel events inside the iframe would be swallowed by its document —
   * forwarded here so pan/zoom works with the cursor over a page. Bound
   * manually (passive: false) to allow preventDefault.
   */
  onDocumentWheel: (e: CanvasWheelInput) => void;
};

const noop = () => undefined;
// Stable empty array for inactive artboards: a fresh `[]` per render would
// bust the overlay hook's `targets` memo and rebind its click capture.
const EMPTY_BLOCKS: EmbeddedBlock[] = [];

/**
 * Memoized: the canvas re-renders on zoom-label changes and the parent passes
 * stable props, so pan/zoom never re-renders the (expensive) artboard subtree.
 */
export const CanvasArtboard = memo(function CanvasArtboard({
  definition,
  html,
  isHtmlLoading,
  blocks,
  blockVariants,
  selectedBlockId,
  onBlockSelect,
  onBlockUpdate,
  interactionMode,
  onVariantEditCommit,
  isActive,
  onActivate,
  onDocumentWheel,
}: CanvasArtboardProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Bind the wheel forwarder inside the iframe document. srcdoc loading is
  // async (and re-runs when the html changes), so poll until the document
  // exists — same approach useShadowDom takes for its own load handling.
  const onDocumentWheelRef = useRef(onDocumentWheel);
  useEffect(() => {
    onDocumentWheelRef.current = onDocumentWheel;
  }, [onDocumentWheel]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let boundDocument: Document | null = null;
    const forward = (e: WheelEvent) => {
      e.preventDefault();
      // The event's client coords are the iframe's own CSS px, relative to
      // its top-left and unscaled — while the canvas scales the artboard.
      // Map them through the iframe's on-screen rect so a zoom anchored on
      // the cursor really stays under the cursor.
      const iframe = wrapper.querySelector('iframe');
      const rect = iframe?.getBoundingClientRect();
      const scaleX = rect && iframe?.clientWidth ? rect.width / iframe.clientWidth : 1;
      const scaleY = rect && iframe?.clientHeight ? rect.height / iframe.clientHeight : 1;
      onDocumentWheelRef.current({
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        clientX: (rect?.left ?? 0) + e.clientX * scaleX,
        clientY: (rect?.top ?? 0) + e.clientY * scaleY,
      });
    };

    const tryBind = () => {
      const iframe = wrapper.querySelector('iframe');
      const doc = iframe?.contentDocument ?? null;
      if (!doc || doc === boundDocument || !doc.body) return;
      boundDocument?.removeEventListener('wheel', forward);
      doc.addEventListener('wheel', forward, { passive: false });
      boundDocument = doc;
    };

    tryBind();
    // srcdoc swaps replace the document object — keep re-checking cheaply.
    const interval = setInterval(tryBind, 1000);

    return () => {
      clearInterval(interval);
      boundDocument?.removeEventListener('wheel', forward);
    };
  }, [html]);

  return (
    <div className="flex flex-col gap-3" onPointerEnter={() => onActivate(definition.key)}>
      {/* Header chip — scaled with the canvas, so keep it large enough to
          read at low zoom. */}
      <div className="flex items-baseline gap-3 px-1">
        <span className="text-2xl font-semibold text-foreground">
          {definition.label}
        </span>
        <span className="text-xl text-muted-foreground">{definition.width}</span>
      </div>
      <div
        ref={wrapperRef}
        className={cn(
          'overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-border',
          isActive && 'ring-2 ring-primary/40',
        )}
        style={{ width: definition.width }}
      >
        <HtmlBlockSelector
          html={html}
          isLoading={isHtmlLoading}
          blocks={isActive ? blocks : EMPTY_BLOCKS}
          blockVariants={blockVariants}
          selectedBlockId={selectedBlockId}
          onBlockSelect={onBlockSelect ?? noop}
          onBlockUpdate={isActive ? onBlockUpdate : undefined}
          interactionMode={isActive ? interactionMode : 'select'}
          onVariantEditCommit={onVariantEditCommit}
          fixedWidth={definition.width}
          viewportHeight={definition.height}
          autoHeight
        />
      </div>
    </div>
  );
});
